import React, { useState } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Divider,
    Stack,
    Tab,
    Tabs,
    TextField,
    IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    AccountBalance as AccountBalanceIcon,
    Description as DescriptionIcon,
    Storage as StorageIcon,
    DataObject as DataObjectIcon,
    Analytics as AnalyticsIcon,
    TrendingUp as TrendingUpIcon,
    Security as SecurityIcon,
    Speed as SpeedIcon,
    CheckCircle as CheckCircleIcon,
    SmartToy as SmartToyIcon,
    Send as SendIcon,
    Code as CodeIcon,
    AttachFile as AttachFileIcon,
    Assessment as AssessmentIcon,
    ShowChart as ShowChartIcon,
    TableChart as TableChartIcon,
    CloudDownload as CloudDownloadIcon,
    Psychology as PsychologyIcon,
    AutoAwesome as AutoAwesomeIcon,
    QueryStats as QueryStatsIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const FinancialAssistant = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [chatMessage, setChatMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([
        {
            type: 'bot',
            message: 'Merhaba! Finansal Dijital Asistan’a hoş geldiniz. SQL veritabanlarınız, Excel tablolarınız veya finansal dokümanlarınız hakkında merak ettiğiniz her şeyi sorabilirsiniz.',
            time: '09:00'
        },
        {
            type: 'user',
            message: 'Son 3 ayın departman bazlı harcama analizini yapıp rapor hazırlar mısın?',
            time: '09:01'
        },
        {
            type: 'bot',
            message: 'Tabii, veritabanı üzerinden son 3 aya ait tüm departman harcamalarını analiz ettim. Pazarlama ve AR-GE departmanlarında bütçe kullanımı %85 seviyesinde seyrederken, operasyonel giderlerde %10 luk bir tasarruf gözlemleniyor. Detaylı raporu PDF formatında hazırladım, aşağıdan indirebilirsiniz.',
            time: '09:01'
        }
    ]);

    // Demo finansal data
    const monthlyRevenue = [
        { month: 'Ocak', gelir: 245000, gider: 180000 },
        { month: 'Şubat', gelir: 280000, gider: 195000 },
        { month: 'Mart', gelir: 310000, gider: 210000 },
        { month: 'Nisan', gelir: 295000, gider: 205000 },
        { month: 'Mayıs', gelir: 340000, gider: 220000 }
    ];

    const categoryDistribution = [
        { name: 'Satışlar', value: 45 },
        { name: 'Hizmetler', value: 30 },
        { name: 'Yatırımlar', value: 15 },
        { name: 'Diğer', value: 10 }
    ];

    const COLORS = ['#1a237e', '#283593', '#3949ab', '#5c6bc0'];

    // Demo mesajlar
    const demoQueries = [
        {
            title: 'SQL Sorgusu',
            query: 'Son 3 ayın gelir raporunu göster',
            icon: <StorageIcon />
        },
        {
            title: 'Doküman Sorgusu',
            query: 'Şirket bütçe politikası nedir?',
            icon: <DescriptionIcon />
        }
    ];

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Ana özellikler
    const mainFeatures = [
        {
            icon: <StorageIcon sx={{ fontSize: 40 }} />,
            title: 'Doğal Dille SQL Sorgulama',
            description: 'Doğal dil ile SQL sorguları çalıştırarak veritabanınızdan anlık veri çekme yeteneği.',
            color: '#1a237e',
            examples: ['SQL sorgula', 'Doğal dil desteği', 'Hızlı analiz']
        },
        {
            icon: <DescriptionIcon sx={{ fontSize: 40 }} />,
            title: 'Doküman Veri Çıkarımı',
            description: 'PDF, Excel, Word gibi dokümanlardan finansal verilerin otomatik çıkarılması.',
            color: '#283593',
            examples: ['PDF okuma', 'Excel analiz', 'Word veri çıkarımı']
        },
        {
            icon: <DataObjectIcon sx={{ fontSize: 40 }} />,
            title: 'Veri Kaynağı Birleştirme',
            description: 'SQL, API, CSV, Excel ve diğer kaynaklardan gelen verileri okuma ve birleştirme.',
            color: '#303f9f',
            examples: ['Çoklu kaynak', 'Veri konsolidasyonu', 'API desteği']
        },
        {
            icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
            title: 'Otomatik Raporlama',
            description: 'Haftalık ve aylık finans raporlarını PDF, Excel veya dashboard formatında üretme.',
            color: '#3949ab',
            examples: ['PDF Rapor', 'Excel çıktı', 'Canlı Dashboard']
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 40 }} />,
            title: 'Hassas Veri Koruması',
            description: 'Rol bazlı yetki, şifreleme ve loglama ile finansal verilerinizi koruma.',
            color: '#3f51b5',
            examples: ['Yetki yönetimi', 'Veri güvenliği', 'Audit log']
        },
        {
            icon: <DataObjectIcon sx={{ fontSize: 40 }} />,
            title: 'Gelişmiş Entegrasyon',
            description: 'JSON ve API entegrasyon desteği ile tüm sistemlerle uyumlu çalışma.',
            color: '#5c6bc0',
            examples: ['REST API', 'JSON desteği', 'Sistem entegrasyonu']
        }
    ];

    // Kullanım senaryoları
    const useCases = [
        {
            icon: <AssessmentIcon />,
            title: 'Aylık Finansal Raporlar',
            description: 'Departmanınızın aylık gelir-gider raporlarını anında alın, bütçe takibi yapın',
            example: 'Bu ayın departman harcamalarını göster'
        },
        {
            icon: <TrendingUpIcon />,
            title: 'Bütçe Analizi',
            description: 'Harcama trendlerini takip edin, bütçe aşımlarını önceden tespit edin',
            example: 'Son 6 ayın bütçe karşılaştırmasını yap'
        },
        {
            icon: <TableChartIcon />,
            title: 'Müşteri Ödemeleri',
            description: 'Müşteri ödemelerini sorgulayın, bekleyen faturaları listeleyin',
            example: 'Ödenmemiş faturaları listele'
        },
        {
            icon: <CloudDownloadIcon />,
            title: 'Politika Sorguları',
            description: 'Şirket finansal politikalarını ve onay süreçlerini sorgulayın',
            example: 'Harcama onay limitleri nedir?'
        },
        {
            icon: <AccountBalanceIcon />,
            title: 'Nakit Akışı Takibi',
            description: 'Günlük nakit akışını kontrol edin, gelecek ödemeleri planlayın',
            example: 'Bu haftanın nakit akışını göster'
        },
        {
            icon: <ShowChartIcon />,
            title: 'Departman Karşılaştırması',
            description: 'Farklı departmanların performansını karşılaştırın, analiz edin',
            example: 'Departmanlar arası harcama karşılaştırması'
        }
    ];

    // Avantajlar
    const benefits = [
        {
            icon: <SpeedIcon />,
            title: 'Hızlı Veri Erişimi',
            description: 'Manuel sorgu süresini azaltır ve veriye erişimi hızlandırır'
        },
        {
            icon: <PsychologyIcon />,
            title: 'Akıllı Yorumlama',
            description: 'Verilerden otomatik içgörü ve öneriler'
        },
        {
            icon: <CheckCircleIcon />,
            title: 'Hata Oranı Azaltma',
            description: 'Manuel işlem hatalarını azaltır ve verimliliği artırır'
        },
        {
            icon: <AutoAwesomeIcon />,
            title: 'Self-Service Analytics',
            description: 'IT ekibine bağımlılığı önemli ölçüde azaltır'
        }
    ];

    const handleSendMessage = () => {
        if (chatMessage.trim()) {
            setChatHistory([...chatHistory,
            {
                type: 'user',
                message: chatMessage,
                time: new Date().toLocaleTimeString().slice(0, 5)
            },
            {
                type: 'bot',
                message: 'Sorgunuzu işliyorum... Veritabanından veri çekiyorum.',
                time: new Date().toLocaleTimeString().slice(0, 5)
            }
            ]);
            setChatMessage('');
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#0f1936ff' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden',
                    background: (theme) => theme.custom.gradients.hero
                }}
            >
                {/* Animated Background */}
                <Box
                    component={motion.div}
                    animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 90, 180, 270, 360],
                        opacity: [0.2, 0.4, 0.2]
                    }}
                    transition={{
                        duration: 25,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    sx={{
                        position: 'absolute',
                        top: -150,
                        right: -150,
                        width: 500,
                        height: 500,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)'
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <AccountBalanceIcon sx={{ fontSize: 50, mr: 2 }} />
                                    <Typography variant="h2" component="h1" sx={{ fontWeight: 800 }}>
                                        Finansal Dijital Asistan
                                    </Typography>
                                </Box>
                                <Chip
                                    label="Yapay Zeka Çözümü"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        mb: 3,
                                        fontSize: '1rem',
                                        py: 3
                                    }}
                                />
                                <Typography variant="h5" sx={{ mb: 4, opacity: 0.95, lineHeight: 1.6 }}>
                                    SQL veritabanları ve finansal dokümanları analiz eden, doğal dil işleme ile karmaşık sorguları çalıştırarak finansal soruları cevaplandıran yapay zeka asistanı
                                </Typography>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                {/* Demo Chat Interface */}
                                <Paper
                                    elevation={10}
                                    sx={{
                                        p: 3,
                                        borderRadius: 4,
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#1e88e5', width: 50, height: 50, mr: 2 }}>
                                            <SmartToyIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ color: '#1e88e5', fontWeight: 600 }}>
                                                Finansal Dijital AsistanAsistan
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                SQL • Doküman
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />

                                    <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2, minHeight: 36 }}>
                                        <Tab icon={<StorageIcon />} label="SQL" sx={{ minHeight: 36, py: 0.5 }} />
                                        <Tab icon={<DescriptionIcon />} label="Doküman" sx={{ minHeight: 36, py: 0.5 }} />
                                    </Tabs>

                                    <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mb: 2, height: 450, overflowY: 'auto' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {chatHistory.map((msg, idx) => (
                                                <Box key={idx} sx={{ alignSelf: msg.type === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                                                    <Paper sx={{ p: 1.5, bgcolor: msg.type === 'user' ? '#1a237e' : 'white', color: msg.type === 'user' ? 'white' : 'black', borderRadius: 2 }}>
                                                        <Typography variant="body2">{msg.message}</Typography>
                                                    </Paper>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: msg.type === 'user' ? 'right' : 'left' }}>
                                                        {msg.time}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>

                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {demoQueries.map((query, index) => (
                                            <Chip
                                                key={index}
                                                icon={query.icon}
                                                label={query.title}
                                                size="small"
                                                clickable
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        ))}
                                    </Stack>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Ana Özellikler */}
            <Box sx={{ py: 8, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            icon={<AutoAwesomeIcon />}
                            label="Ana Özellikler"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#283593' }}>
                            Çoklu Veri Kaynağı Desteği
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                            SQL ve Dokümanlardan akıllıca veri çeken yapay zeka
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {mainFeatures.map((feature, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            minHeight: '240px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            '&:hover': {
                                                boxShadow: 8,
                                                transform: 'translateY(-5px)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, flex: 1 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: feature.color,
                                                    width: 64,
                                                    height: 64,
                                                    mr: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                {feature.icon}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    gutterBottom
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: feature.color,
                                                        minHeight: '56px',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    {feature.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2, lineHeight: 1.6 }}
                                                >
                                                    {feature.description}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                    {feature.examples.map((example, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={example}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: `${feature.color}15`,
                                                                color: feature.color,
                                                                fontSize: '0.7rem'
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Kullanım Senaryoları */}
            <Box sx={{ py: 8, bgcolor: '#f5f7fa' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Chip
                            icon={<QueryStatsIcon />}
                            label="Kullanım Senaryoları"
                            color="primary"
                            sx={{ mb: 2, fontSize: '1rem', py: 3 }}
                        />
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: '#283593' }}>
                            Asistanın Yanıtlayabildiği Konular
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {useCases.map((useCase, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                            minHeight: '260px',
                                            '&:hover': {
                                                boxShadow: 6,
                                                transform: 'scale(1.05)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: '#1e88e5',
                                                width: 64,
                                                height: 64,
                                                mx: 'auto',
                                                mb: 2
                                            }}
                                        >
                                            {useCase.icon}
                                        </Avatar>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                fontWeight: 600,
                                                minHeight: '64px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {useCase.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 2, flex: 1, lineHeight: 1.6 }}
                                        >
                                            {useCase.description}
                                        </Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                                            <Typography variant="caption" sx={{ color: '#1e88e5', fontStyle: 'italic' }}>
                                                "{useCase.example}"
                                            </Typography>
                                        </Paper>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>



            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default FinancialAssistant;

